import React, { useContext } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Button,
  mergeClasses,
  Text,
  Title1,
  tokens,
} from '@fluentui/react-components';
import {
  PersonAddRegular,
  PersonRegular,
} from '@fluentui/react-icons';

import { useUserInvitationSelectionPageStyles } from './use-user-invitation-selection-page-styles';

type InvitationChoiceButtonProps = {
  className: string;
  icon: React.ReactElement;
  title: string;
  description: string;
  onClick: () => void;
};

const InvitationChoiceButton: React.FC<InvitationChoiceButtonProps> = ({
  className, icon, title, description, onClick,
}) => {
  const classes = useUserInvitationSelectionPageStyles();
  return (
    <Button className={mergeClasses('invitation-choice-button', className, classes.choiceButton)} onClick={onClick}>
      <span className={classes.choiceButtonIcon} aria-hidden>{icon}</span>
      <span className={classes.choiceButtonText}>
        <Text className={classes.choiceButtonTitle} weight="semibold">{title}</Text>
        <Text className={classes.choiceButtonDescription} size={200}>{description}</Text>
      </span>
    </Button>
  );
};

export type InvitationChoiceViewProps = {
  invitationSummary?: string | null;
  daysLeftPill?: React.ReactNode;
  onCreate: () => void;
  onSignIn: () => void;
};

// First view: pick between creating a new account or using an existing one.
export const InvitationChoiceView: React.FC<InvitationChoiceViewProps> = ({
  invitationSummary, daysLeftPill, onCreate, onSignIn,
}) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useUserInvitationSelectionPageStyles();

  return (
    <div className={classes.textAndButtons}>
      {daysLeftPill && <div className={classes.invitationMeta}>{daysLeftPill}</div>}
      <Title1 as="h1" block className={mergeClasses('idp-title', classes.title)}>
        {i18n.getString(_TL_('You\'ve been invited to join Microsoft Advertising.'))}
      </Title1>
      {invitationSummary && (
        <Text block size={300} color={tokens.colorNeutralForeground2} className="invitation-summary">
          {invitationSummary}
        </Text>
      )}
      <Text block size={300} color={tokens.colorNeutralForeground2}>
        {i18n.getString(_TL_('Sign in with an existing account, or use a different email to get started.'))}
      </Text>
      <div className={mergeClasses('invitation-choice-list', classes.choiceList)}>
        <InvitationChoiceButton
          className="invitation-existing-account-choice"
          icon={<PersonRegular />}
          title={i18n.getString(_TL_('Use an existing account'))}
          description={i18n.getString(_TL_('Sign back in with an email you\'ve used before'))}
          onClick={onSignIn}
        />
        <InvitationChoiceButton
          className="invitation-create-account-choice"
          icon={<PersonAddRegular />}
          title={i18n.getString(_TL_('Create an account'))}
          description={i18n.getString(_TL_('Sign up or set up another account using a different email address'))}
          onClick={onCreate}
        />
      </div>
    </div>
  );
};
